import {
  reportNotifyOutcome,
  showSystemNotification,
  type NotifyResult,
  type SystemNotificationInput,
} from './dispatch'
import { deveNotificarSistemaAgora } from './foco'
import { getNotificationPrefs } from './prefs'

const SOUND_PATH = '/sounds/notification.mp3'
const SOUND_VOLUME = 0.5

/**
 * Som do aviso, se o usuário quis som. Nunca levanta: autoplay bloqueado e
 * ambiente sem `Audio` são normais, e o aviso VISUAL continua valendo sem ele —
 * derrubar o caminho de notificar por causa do som seria trocar o aviso todo
 * pelo enfeite.
 */
export function playNotificationSound(): void {
  if (!getNotificationPrefs().soundEnabled) return
  try {
    const audio = new Audio(SOUND_PATH)
    audio.volume = SOUND_VOLUME
    void audio.play().catch(() => {
      /* autoplay bloqueado — o aviso visual continua valendo */
    })
  } catch {
    /* ambiente sem Audio */
  }
}

/**
 * A costura entre a preferência global e o funil do SO, compartilhada pelos TRÊS
 * produtores de aviso ([[../../hooks/useNotification]], origem `chat`;
 * [[../../hooks/useChannelActivityToast]], origem `canal`;
 * [[../../hooks/notifications/useBrowserNotifications]], origem `inbox`).
 *
 * O botão do painel de diagnóstico (origem `teste`) passa por aqui também,
 * com `ignorarFoco` — ele é apertado com a janela na frente por definição, e
 * sem isso devolveria `skipped-tab-visible` sempre. `browserEnabled` continua
 * valendo nele: um teste que pulasse essa porta mentiria sobre o sistema que
 * existe pra diagnosticar.
 * (`app/(private)/conta/_paineis/notificacoes/DiagnosticoNotificacoes.tsx:211`), de
 * propósito — ele existe pra provar que o funil do SO funciona, e fazê-lo parar
 * em `disabled-by-pref` responderia a pergunta errada.
 *
 * Quatro coisas, e todas já foram esquecidas por um call site:
 *
 * 1. **`browserEnabled` governa.** Sem esta porta, cada produtor novo precisa
 *    lembrar de consultar a preferência — e o `useNotification` passou meses sem
 *    consultar, com a tela de preferências prometendo governar o que não
 *    governava.
 * 2. **Todo caminho reporta**, inclusive o "nem tentou". Quem lê é a tela de
 *    preferências, porque a bancada é um celular e num celular não há DevTools.
 * 3. **O resultado VOLTA** para quem chamou decidir o fallback (D-2): toast sai
 *    só quando a notificação do SO não saiu, pelo `outcome` tipado — nunca por
 *    heurística de visibilidade.
 * 4. **A regra de foco e atividade** ([[foco]]) vale igual para os três. Ela vive
 *    aqui, e não em cada produtor, pelo mesmo motivo do item 1: aplicar num só
 *    criaria duas realidades — dois avisos do mesmo app discordando sobre onde
 *    aparecer, dependendo de qual socket os trouxe.
 *
 * O que NÃO está aqui, de propósito: reduzir conteúdo. Quem reduz é o funil
 * (`showSystemNotification`), justamente para que nenhum call site possa
 * esquecer de reduzir.
 */
export async function notifySystem(
  input: SystemNotificationInput,
  origem: string,
  opcoes: NotifySystemOptions = {},
): Promise<NotifyResult> {
  const resultado = await decidir(input, opcoes)
  reportNotifyOutcome(resultado, origem)
  return resultado
}

export interface NotifySystemOptions {
  /**
   * Vai para a bandeja mesmo com a janela na frente.
   *
   * Existe por causa de UM caso: o `critical` do inbox. Urgência não espera o
   * operador tirar o foco da tela, e o aviso crítico já é aquele que o produto
   * decidiu disparar contra as prefs POR KIND do usuário ([[prefs]]).
   *
   * **Não fura mais nada.** `browserEnabled` continua valendo, e o motivo é o
   * mesmo do nível de conteúdo ([[dispatch]]): furar o foco decide sobre a
   * atenção de quem já pediu para ser avisado; furar as demais portas decidiria
   * outra coisa. Um furo por vez, cada um com a sua razão escrita.
   */
  ignorarFoco?: boolean
}

/**
 * A ordem das portas é decisão, não acaso: `browserEnabled` vem ANTES do foco.
 *
 * As duas calam o aviso, mas só uma o usuário resolve numa tela. Reportar
 * `skipped-tab-visible` para quem simplesmente não ligou a notificação mandaria
 * o diagnóstico de Minha conta › Notificações explicar o estado transitório e esconder
 * o permanente — e o foco passa sozinho no segundo seguinte.
 */
async function decidir(
  input: SystemNotificationInput,
  { ignorarFoco = false }: NotifySystemOptions,
): Promise<NotifyResult> {
  if (!getNotificationPrefs().browserEnabled) return { outcome: 'disabled-by-pref' }
  if (!ignorarFoco && !deveNotificarSistemaAgora()) return { outcome: 'skipped-tab-visible' }
  return showSystemNotification(input)
}
